import React from 'react';
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { User, Settings, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Lock, CreditCard, Zap, AlertTriangle, CheckCircle } from 'lucide-react';
import Footer from '@/components/Footer';
import { Button } from '../components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import { getLoginPath, getPricingPath, getSettingsPath } from '@/utils/localePaths';

function GuvenlikPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, locale } = useTranslation();

  // Prose icindeki ||...|| isaretli kelimeleri <strong>'a cevirir (i18n metni + vurgu korunur)
  const richText = (key) => t(key).split('||').map((seg, i) => (
    i % 2 === 1 ? <strong key={i} className="text-[#FFDD59]">{seg}</strong> : seg
  ));

  const getPlanDisplayName = (planType) => {
    const planNames = {
      'free': t('security.plan_free'),
      'pro_monthly': t('security.plan_pro'),
      'business_monthly': t('security.plan_business'),
      'persona_staff': t('security.plan_staff')
    };
    return planNames[planType] || planType;
  };


  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-b from-black via-gray-900 to-black pt-2 pb-1 px-4">
        <div className="mb-1 px-4">
          <div className="flex justify-between items-center gap-4 bg-gray-900/50 backdrop-blur-sm rounded-lg p-2 border border-gray-800">
            <div 
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => navigate('/')}
                >
                  <img 
                    src="/opara-logo-trimmed.png" 
                    alt="OPARA Logo" 
                    className="h-10 w-auto object-contain"
                  />
                </div>
            {user && (
              <div className="flex items-center gap-2">
                {/* Analiz Hakkı */}
                <div className="text-sm bg-gray-800 px-3 py-1 rounded-lg border border-gray-700">
                  <span className="text-white">{t('security.credit_label')} </span>
                  <span className="text-white font-bold">
                    {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining}
                  </span>
                </div>
                
                {/* Paket Yükle Butonu */}
                <Link to={getPricingPath(locale)}>
                  <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold py-1 text-sm">
                    {t('security.buy_package')}
                  </Button>
                </Link>
                
                {/* Avatar Dropdown Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFDD59] to-[#E6C84D] flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer border-2 border-gray-700 hover:border-[#FFDD59]">
                      <User className="w-4 h-4 text-black" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700" align="end">
                    <DropdownMenuLabel className="text-white">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">
                          {user?.first_name} {user?.last_name}
                          {user?.plan_type === "persona_staff" && (
                            <span className="text-xs text-[#FFDD59] ml-2">{t('security.staff_badge')}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">
                          {t('security.plan_label')} <span className="text-[#FFDD59]">{getPlanDisplayName(user?.plan_type)}</span>
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    <DropdownMenuItem asChild className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800">
                      <Link to={getSettingsPath(locale)} className="flex items-center">
                        <Settings className="w-4 h-4 mr-2" />
                        {t('security.account_settings')}
                      </Link>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-gray-800">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('security.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Sağ: Butonlar (giriş yapmamış kullanıcılar için) */}
            {!user && (
              <div className="flex items-center gap-2">
                <Link to={getPricingPath(locale)}>
                  <Button 
                    className="bg-[#FFDD59] hover:bg-[#FFE875] text-black font-bold w-40 py-1 text-sm"
                  >
                    {t('security.packages_btn')}
                  </Button>
                </Link>
                
                <Link to={getLoginPath(locale)}>
                  <Button 
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold w-40 py-1 text-sm"
                  >
                    {t('security.login_btn')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Geri Dön Butonu - Header altında, header ile aynı hizada */}
      <div className="px-4 mb-2">
        <div className="px-4">
          <Button 
            onClick={() => navigate(-1)}
            className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
          >
            {t('common.back')}
          </Button>
        </div>
      </div>

      <div className="relative z-10 py-4 px-4">
        <div className="max-w-5xl mx-auto">

          {/* Hero Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#FFDD59] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('security.h1_title')}
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed">
              {t('security.intro')}
            </p>
          </div>

          {/* Main Content */}
          <div className="space-y-6">

            {/* Section 1 */}
            <Card className="bg-gray-900 border-2 border-[#FFDD59]">
              <CardContent className="p-8">
                <h2 className="text-xl font-bold text-[#FFDD59] mb-6 flex items-center gap-3">
                  <Lock className="w-6 h-6" />
                  {t('security.s1_title')}
                </h2>
                
                <div className="space-y-6">
                  {/* Şifre Koruması */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-[#FFDD59]" />
                      {t('security.s1_pw_title')}
                    </h3>
                    <p className="text-gray-300 ml-7 leading-relaxed">
                      {richText('security.s1_pw_text')}
                    </p>
                  </div>

                  {/* Yetkilendirme */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-[#FFDD59]" />
                      {t('security.s1_auth_title')}
                    </h3>
                    <p className="text-gray-300 ml-7 leading-relaxed">
                      {t('security.s1_auth_text')}
                    </p>
                  </div>

                  {/* Kredi Kartı Güvenliği */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-[#FFDD59]" />
                      {t('security.s1_cc_title')}
                    </h3>
                    <p className="text-gray-300 ml-7 leading-relaxed">
                      {richText('security.s1_cc_text')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 2 */}
            <Card className="bg-gray-900 border-2 border-[#FFDD59]">
              <CardContent className="p-8">
                <h2 className="text-xl font-bold text-[#FFDD59] mb-6 flex items-center gap-3">
                  <Shield className="w-6 h-6" />
                  {t('security.s2_title')}
                </h2>
                
                <div className="space-y-6">
                  {/* Hız Sınırlaması */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#FFDD59]" />
                      {t('security.s2_rate_title')}
                    </h3>
                    <p className="text-gray-300 ml-7 leading-relaxed">
                      {t('security.s2_rate_text')}
                    </p>
                  </div>

                  {/* Saldırı Koruması */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-[#FFDD59]" />
                      {t('security.s2_attack_title')}
                    </h3>
                    <p className="text-gray-300 ml-7 leading-relaxed">
                      {richText('security.s2_attack_text')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 3 */}
            <Card className="bg-gray-900 border-2 border-[#FFDD59]">
              <CardContent className="p-8">
                <h2 className="text-xl font-bold text-[#FFDD59] mb-6 flex items-center gap-3">
                  <Lock className="w-6 h-6" />
                  {t('security.s3_title')}
                </h2>
                
                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p>
                    {richText('security.s3_text1')}
                  </p>
                  <p>
                    {richText('security.s3_text2')}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Final Message */}
            <Card className="bg-gradient-to-r from-gray-900 to-gray-800 border-2 border-[#FFDD59]">
              <CardContent className="p-8">
                <div className="text-center">
                  <Shield className="w-12 h-12 text-[#FFDD59] mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-[#FFDD59] mb-4">
                    {t('security.final_title')}
                  </h2>
                  <p className="text-gray-300 text-lg mb-4 leading-relaxed">
                    {t('security.final_text1')}
                  </p>
                  <p className="text-gray-400">
                    {t('security.final_contact').split('{EMAIL}')[0]}<a href="mailto:destek@opara.co" className="text-[#FFDD59] hover:underline font-semibold">destek@opara.co</a>{t('security.final_contact').split('{EMAIL}')[1]}
                  </p>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default GuvenlikPage;
